import { after, cloneDeepWith, isArray, isObject, omitBy } from "lodash";
import { IntlShape } from "react-intl/src/types";
import { FormInstance } from "antd";
import dayjs from "dayjs";

import DOMPurify from "dompurify";

function omitDeep(collection: Object | [], omitFn: (val, key) => Boolean, maxDepth: number = 10) {
  let currentDepth = 0;

  return cloneDeepWith(collection, function customizer(v) {
    currentDepth++;

    if (currentDepth > maxDepth) {
      console.warn(`omitDeep: Maximum depth of ${maxDepth} exceeded. Returning value as-is to prevent stack overflow.`);
      currentDepth--;
      return v;
    }

    const result = isArray(v)
      ? v.map((e) => (isObject(e) ? cloneDeepWith(e, customizer) : e))
      : isObject(v)
        ? cloneDeepWith(omitBy(v, omitFn), after(2, customizer))
        : undefined;

    currentDepth--;
    return result;
  });
}

export {
  omitDeep
};

export const getI18n = (id: string, defaultMessage: string, intl: IntlShape) => {  
  if (!intl) {
    return '';
  }

  if(id === null || id === undefined){
    console.error(`getI18n error - id is null:: ${defaultMessage}`); 
  }
  return intl.formatMessage({
    id: id ?? "no-id-supplied", defaultMessage
  });
};

export const isUnauthorizedError = (error: any): boolean => {
  const statusCode = error?.networkError?.statusCode || error?.networkError?.status || error?.statusCode || error?.status;
  const messageText = String(error?.message || error?.networkError?.message || "");

  return statusCode === 401 || messageText.includes("401");
};

export const readNestedFormValue = (form:FormInstance<any>, nestedProp:string)=> {
  const nestedProperties = nestedProp.split('.');
  let fieldValues = form.getFieldsValue();
  let result = undefined;
  for (let i = 0; i < nestedProperties.length; i++) {
    result = fieldValues[nestedProperties[i]];
    if (!result) {
      break;
    }
    if (typeof result === "object") {
      fieldValues = result;
    }
  }

  return result;
}

/**
 * Generate a nested value, and save it in FormInstance.
 * @param nestedProp
 * @param nestedValue
 */
export const createdNestedValue = (nestedProp:string, nestedValue:any)=> {
  const nestedProperties = nestedProp.split('.');
  const value = {};
  let previous = value;
  for (let i = 0; i < nestedProperties.length; i++) {
    if (i < nestedProperties.length-1) {
      previous = {};
      value[nestedProperties[i]] = previous;
    }
    else {
      previous[nestedProperties[i]] = nestedValue;
    }
  }

  return value;
};

export function isNotNull(val: any) {
  return val !== undefined && val !== null;
}


export const isExternalUser = (entitlements: string[]) => { 
  return !entitlements?.includes("user@internal");  //Rather do negative test IF NOT INTERNAL - so if internal does not exists - assume external. will fix any issue if no entitlements exist.
}

export function peek<T>(val:T) {
  console.log('array item peek',val);
  return val;
}

export function renderPeek(val:any) {
  console.log('render peek',val);
  return true;
}

export function groupByKeyMapper(arr:any[], keyMapper:(item:any)=>string, valueMapper?:(item:any)=>any):Record<string, any[]> {
  return arr.reduce((result, arrayItem) => {
    const key = keyMapper(arrayItem);
    if (!result[key]) {
      result[key] = [];
    }

    const value = valueMapper ? valueMapper(arrayItem) : arrayItem;

    result[key].push(value);

    return result;
  }, {});
}

export type CustomReducer = {
  reducer: (result,ithItem)=>any;
  initial:any;
}

export function groupByKeyMapperCustomerReducer(
    arr:any[],
    keyMapper:(item:any)=>string,
    customReducer:CustomReducer,
    valueMapper?:(item:any)=>any) {

  const original = groupByKeyMapper(arr,keyMapper,valueMapper);
  const newMap = {};

  for (const key in original) {
    const values = original[key];
    newMap[key] = values.reduce(customReducer.reducer,customReducer.initial);
  }

  return newMap;
}

export function roundNumber(numberToRound:number, precision:number) {
  if (precision===0) {
    return `${Math.round(numberToRound)}`;
  }

  if (numberToRound!==undefined && numberToRound!==null) {
    return (Math.round(numberToRound*Math.pow(10,precision))/Math.pow(10,precision)).toFixed(precision)
  }
  return '';
}

export function getMinDate(firstDate:dayjs.Dayjs, secondDate:dayjs.Dayjs) {
  if (firstDate && secondDate) {
    if (secondDate.isBefore(firstDate)) {
      return secondDate;
    }
  }

  return firstDate;
}

export function convertStringToNumber(numberAsString:string) {
    if (numberAsString) {
        try {
            return parseFloat(numberAsString);
        }
        catch(error) {
            console.error('Not valid number', numberAsString);
        }
    }

    return null;
}

export function convertStringToProbability(probabilityAsString:string) {
    const number = convertStringToNumber(probabilityAsString);
    if (number!==null) {
        return number/100;
    }
}

export function getMaxDate(firstDate:dayjs.Dayjs, secondDate:dayjs.Dayjs) {
  if (firstDate && secondDate) {
    if (secondDate.isAfter(firstDate)) {
      return secondDate;
    }
  }

  return firstDate;
}

export function removeScriptsFromText(text:string) {
  return DOMPurify.sanitize(text, {FORBID_TAGS:['style']});
}

/*
The method below converts confluence markup for bolding, italizing, underlining text and specifying links into html.
Links are resolved first so that underscores (and other markup characters) inside URLs are never misinterpreted
as inline formatting.
* */
export function interpretConfluenceMarkup(originalText: string): string {
    let text = originalText;
    // Define regular expressions for different markup types
    const boldRegex = /\*([^*]+)\*/g;
    const italicRegex = /_([^_]+)_/g;
    const underlineRegex = /\+([^+]+)\+/g;
    const linkRegex = /\[([^|]+)\|([^\]]+)\]/g;

    // Replace link markup using placeholders so that the rendered <a> tags are
    // never touched by the bold/italic/underline regexes that run afterwards.
    // Without placeholders the italic regex (_…_) would corrupt underscores
    // that appear inside URLs (e.g. https://example.com/some_path_here).
    // Inline markup rules are still applied to the link label only.
    const anchors: string[] = [];
    text = text.replace(linkRegex, (match, label, url) => {
        const formattedLabel = label
            .replace(boldRegex, (_m: string, p1: string) => `<strong>${p1}</strong>`)
            .replace(italicRegex, (_m: string, p1: string) => `<em>${p1}</em>`)
            .replace(underlineRegex, (_m: string, p1: string) => `<u>${p1}</u>`);
        const anchor = `<a class="edp-link" href="${url.trim()}" target="_blank">${formattedLabel}</a>`;
        const placeholder = `\x00ANCHOR${anchors.length}\x00`;
        anchors.push(anchor);
        return placeholder;
    });

    // Replace bold markup
    text = text.replace(boldRegex, (match, p1) => `<strong>${p1}</strong>`);

    // Replace italic markup
    text = text.replace(italicRegex, (match, p1) => `<em>${p1}</em>`);

    // Replace underline markup
    text = text.replace(underlineRegex, (match, p1) => `<u>${p1}</u>`);

    // Restore anchors now that all other replacements are done
    text = text.replace(/\x00ANCHOR(\d+)\x00/g, (_m, i) => anchors[Number.parseInt(i, 10)]);

    return text;
}

export function multipleValueSplitHelper(value: string) {
    // Start with newline-split values
    const values: string[] = value.split(/\n/);

    // Expand by splitting on commas without mutating the iteration array
    let valuesWithCommas: string[] = [...values];
    for (const singleValue of values) {
        if (singleValue?.length) {
            const commaSplitValues = singleValue.trim().split(/,/);
            if (commaSplitValues.length > 1) {
                for (const commaSplitValue of commaSplitValues) {
                    if (commaSplitValue.length) {
                        valuesWithCommas.push(commaSplitValue);
                    }
                }
            }
        }
    }

    valuesWithCommas =
        valuesWithCommas
            .filter(val=>val?.length)
            .filter(val=>val.indexOf(',')<0);

    // Expand by splitting on spaces without mutating the iteration array
    let valuesWithSpaces: string[] = [...valuesWithCommas];
    for (const singleValue of valuesWithCommas) {
        const spaceSplitValues = singleValue.split(/\s/);
        if (spaceSplitValues.length > 1) {
            for (const spaceSplitValue of spaceSplitValues) {
                valuesWithSpaces.push(spaceSplitValue);
            }
        }
    }

    //why the below
    //Values with spaces typically won't have more than 2 characters.
    valuesWithSpaces = valuesWithSpaces
                        .filter((val) => val?.length)
                        .filter(val=>val.split(/\s/).length < 4);

    return Array.from(
        new Set(valuesWithSpaces
            .map(val => val?.trim())
            .filter(value => value?.length)));
}

